import { apiClient } from "@/services/apiClient";

export type TaxCalendarSource = "iva_minimum" | "dgi_calendar" | "bps_calendar";

export interface DivergenceItem {
  source: TaxCalendarSource;
  label: string;
  hardcoded: string;
  scraped: string;
}

/** How the latest scrape read the page: the deterministic parser, or the
 *  AI fallback that recovered it after the parser broke. */
export type TaxCalendarParsedVia = "parser" | "ai_fallback";

export interface SourceReport {
  source: TaxCalendarSource;
  scrapedAt: string | null;
  /** URL the last scrape actually hit — the DGI calendar page is
   *  resolved per run, so this is not a constant. */
  sourceUrl: string | null;
  ok: boolean;
  errorMessage: string | null;
  parsedVia: TaxCalendarParsedVia;
  divergences: DivergenceItem[];
  alert: boolean;
}

export interface DivergenceReport {
  perSource: Record<TaxCalendarSource, SourceReport>;
  totalDivergences: number;
  totalAlerts: number;
}

export interface TaxCalendarTrackerStatus {
  report: DivergenceReport;
  referenceYear: number;
  hardcoded: {
    ivaMinimum: { year: number; full: number; tier50: number; tier25: number } | null;
    dgi2026: {
      cede: Record<string, number>;
      noCede: Record<string, number>;
      ivaMinimoOrd4: Record<string, number>;
    } | null;
  };
}

/** Root-only: current scraper status + hardcoded reference values. */
export async function getTaxCalendarTrackerStatus(): Promise<TaxCalendarTrackerStatus> {
  const { data } = await apiClient.get<TaxCalendarTrackerStatus>(
    "/platform/tax-calendar-tracker/status",
  );
  return data;
}

/** Root-only: force a fresh scrape of all three sources. Returns when
 *  the slowest scrape finishes (sequential to avoid hammering gub.uy). */
export async function refreshTaxCalendarTracker(): Promise<void> {
  await apiClient.post("/platform/tax-calendar-tracker/refresh");
}
